/**
 * カートの「本体」と「工事」を1組として扱う（日高さん D3・D5・2026-08-19）。
 *
 * ★ なぜ要るのか ★
 * 工事つきの購入は本体と工事を**別々の行**でカートに入れる（できますよ と同じ見せ方）。
 * 別々の行ということは、片方だけ消せてしまう。工事だけ残ると「工事だけ買う」注文になり、
 * 本体だけ残すと工事の手配が漏れる。どちらも起きてはいけない。
 *
 * ★ 片方が消えたら、もう片方も消す（D5）★
 * できますよ は注意書きだけで工事だけ削除できる状態だが、日高さんの指示は
 * 「本体を消したら一緒に工事も消える」。ここでは**どちら側から消しても**組ごと消す。
 *
 * ★ 削除の横取りではなく、状態の突き合わせで直す ★
 * テーマの削除ボタンを乗っ取るのはやめた。数量を 0 にする / 数量ボタン / カートドロワー /
 * 別タブでの操作、と入口が複数あり、1つでも取りこぼすと片側だけ残る。
 * 代わりにカートが変わるたびに `/cart.js` を読み直し、
 * **相方のいない行を消す**（本体だけ・工事だけ、のどちらも）。入口が増えても効き続ける。
 *
 * ★ 数量も揃える ★
 * 本体2台なら工事も2件。片方だけ増減されたら少ないほうに合わせる。
 */

const INSTALL_HANDLE = 'aicon-standard-install';
/**
 * 延長保証（任意・2026-08-20）。
 * 本体に対する保証なので、本体が消えたら一緒に消す。工事とは独立で、
 * 「本体のみ＋保証」も成立する（工事が無くても保証は買える）。
 */
const WARRANTY_HANDLE = 'aicon-extended-warranty';
/**
 * AI画像判定で出た追加工事（2026-08-20）。
 * 工事つき本体に対する追加なので、本体が消えたら一緒に消す。
 * 単価110円 × 数量で金額を表しているため、**数量を本体数に揃えてはいけない**
 * （揃えると請求額が変わる）。消すか残すかだけを判断する。
 */
const ADDITIONAL_HANDLE = 'aicon-additional-work';

function routeRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/** 工事の行か。handle で見る（タイトルは管理画面で変えられるため当てにしない） */
function isInstallLine(line) {
  return line.handle === INSTALL_HANDLE;
}

/**
 * 工事を伴う本体の行か。
 *
 * ★ 本体は2バリアントある ★
 *   本体（工事つき） … requires_shipping = false（自社搬入。工事の行が必ず要る）
 *   本体のみ         … requires_shipping = true（宅配。工事は無い）
 * タイトルではなく requires_shipping で見る。option 名は管理画面で変えられるが、
 * 送るか自社で運ぶかは配送料の計算に直結するので勝手には変わらない。
 */
function needsInstall(line) {
  return isBodyLine(line) && line.requires_shipping === false;
}

/** 追加工事の行か */
function isAdditionalLine(line) {
  return line.handle === ADDITIONAL_HANDLE;
}

/** 保証の行か */
function isWarrantyLine(line) {
  return line.handle === WARRANTY_HANDLE;
}

/**
 * AiCON が売るエアコン本体の行か。
 *
 * ★ 否定形（「工事でも保証でもない行」）で書いてはいけない（2026-08-25 修正）★
 *   以前は `!isInstallLine && !isWarrantyLine && !isAdditionalLine` だった。
 *   これだと **AiCON が知らない商品まで本体扱い**になる。とくに Shopify の
 *   ギフトカードは常に `requires_shipping: false` なので `needsInstall()` を満たし、
 *   `pairedBodies` に混入する。このテーマはギフトカードを実際に扱う
 *   （templates/gift_card.liquid・blocks/buy-buttons.liquid:69,237）。
 *
 *   起きること（工事つき本体1 ＋ 標準工事1 ＋ 追加工事33 ＋ ギフトカード1）:
 *     needQty(本体1+ギフト1=2) > installQty(1) → reduceTo(pairedBodies, 1)
 *     → cart.items 順の先頭にある **エアコン本体のほうが 0 になる**
 *     → 次のパスでも pairedBodies にギフトカードが残るので追加工事も消えない
 *     → 最終カート = 標準工事 ¥25,590 ＋ 追加工事 ¥3,630 ＋ ギフトカード
 *       本体が無いのに工事費だけ ¥29,220 を請求できてしまう。
 *     しかも 2026-08-25 に工事・追加工事の行から 🗑 とステッパーを外したため、
 *     **お客様が自分で消せない**（snippets/cart-products.liquid）。
 *   ギフトカード単独のカートでも reduceTo が黙って消してリロードする。
 *
 * ★ 肯定形で見る ★
 *   product_type は backend が付けている（product-mapper.ts:334 = 'エアコン' /
 *   install-product.ts・additional-work-product.ts = '工事' / warranty-product.ts = '保証'）。
 *   `/cart.js` の line item は product_type を返す（本番で実測済み）。
 *   **自分が管理していると確信できる行以外の quantity は絶対に 0 にしない。**
 *
 *   なお snippets/aicon-cart-line-rank.liquid の既定が 'body' なのは正しい。
 *   あちらは「未知の商品にもステッパーと 🗑 を出す」＝触れるようにする話で、
 *   こちらの「未知の商品を消してよいか」とは逆向きの判断になる。
 */
const BODY_PRODUCT_TYPE = 'エアコン';

function isBodyLine(line) {
  return line.product_type === BODY_PRODUCT_TYPE;
}

/**
 * 本体と工事を1組として結ぶ line item property の名前。
 *
 * ★ カート投入の全経路で同じ文字列を使う ★
 *   assets/aicon-install-quote.js   … 診断完走（checkout）／写真なしで進む（contactStaff）
 *   assets/aicon-return.js          … 診断からの復帰
 *   sections/aicon-product-buybox.liquid … 購入ボックスの submit
 *   ずれると組を引けず、下の「組ID を持たない行」の扱いに落ちる。
 *   ずれは scripts/check-aicon-cart-set.mjs が CI で止める。
 *
 * ★ アンダースコア始まりのキーはカート・注文画面に出ない（Shopify の仕様）★
 *   お客様には見えず、管理画面の注文明細では読める。
 */
const SET_PROPERTY = '_aicon_set';

/**
 * 行の組ID。持たない行は null。
 *
 * ★ Cart Transform でまとめた行は properties を持たない（2026-08-26 修正）★
 *   `extensions/aicon-cart-transform` が追加工事を「一式 ×1」にまとめると、
 *   まとめた親行に残るのは merge の attributes だけで、
 *   **子行の properties（費目・組ID）は item_components 側へ移る**。
 *   sections/aicon-cart-breakdown.liquid:391 が費目で同じことをしている。
 *
 *   組ID を子から引かないと、まとめた追加工事は「組ID を持たない行」の
 *   まとまりへ落ち、本体・標準工事とは**別グループ**になる。そのグループには
 *   工事つき本体が1つも居ないため `pairedQtyForAdditional === 0` が成立し、
 *   入れた直後の追加工事が全部 0 にされる。
 *
 *   実際に起きたこと（2026-08-26 日高さん報告）:
 *     写真判定 → カートに入れる → 追加工事 ¥16,720 が一瞬出る → 消える
 *     → 本体と標準工事だけが残り、写真判定した意味が無くなる
 *   標準工事が残るのは、本体と同じ組で数が釣り合っているため。
 */
function setIdOf(line) {
  const direct = (line.properties || {})[SET_PROPERTY];
  if (typeof direct === 'string' && direct !== '') return direct;

  for (const component of line.item_components || []) {
    const value = (component.properties || {})[SET_PROPERTY];
    if (typeof value === 'string' && value !== '') return value;
  }
  return null;
}

/**
 * 行を組ごとに分ける。
 *
 * ★ なぜ組が要るのか（2026-08-25・複数台）★
 *   以前は**カート全体の合計数**だけを見て「多いほうを減らす」を行い、
 *   減らす相手は cart.items 順の先頭から取っていた。**どの工事がどの本体のものか**を
 *   持っていないため、機種の違う2台をカートに入れると取り違える。
 *
 *   実測（本体A 5.6kW未満＋工事A ¥26,590 ／ 本体B 6.3kW以上＋工事B ¥31,590）:
 *     工事B を消す → needQty(2) > installQty(1) → 先頭の **本体A** が消える
 *       → 残り 本体B ＋ 工事A で工事費 ¥26,590（正しくは ¥31,590）＝ ¥5,000 の請求漏れ
 *     本体B を消す → 先頭の **工事A** が消える
 *       → 残り 本体A ＋ 工事B で ¥31,590（正しくは ¥26,590）＝ ¥5,000 の過大請求
 *   どちらも本体1：工事1で辻褄が合うため、**この関数は異常と判断できない**。
 *
 *   組ID で分ければ、減らす相手は必ず同じ組の中から選ばれる。
 *
 * ★ 組ID を持たない行は、まとめて1つの組として扱う ★
 *   この変更より前に作られたカートには組ID が無い。個別の組に分けると
 *   「本体だけの組」「工事だけの組」になり、**お客様のカートを勝手に空にする**。
 *   まとめておけば、従来どおり合計数で突き合わせる動きになる（挙動は変えない）。
 */
function groupBySet(lines) {
  const bySet = new Map();
  const withoutSet = [];
  for (const line of lines) {
    const id = setIdOf(line);
    if (id === null) {
      withoutSet.push(line);
      continue;
    }
    if (!bySet.has(id)) bySet.set(id, []);
    bySet.get(id).push(line);
  }
  const groups = [...bySet.values()];
  if (withoutSet.length > 0) groups.push(withoutSet);
  return groups;
}

/**
 * カートを整える。
 *
 * @returns 実際に直したら true（呼び出し側は画面を作り直す）
 */
async function reconcile() {
  const root = routeRoot();
  const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) return false;

  const cart = await res.json();
  const lines = cart.items || [];
  let changed = false;

  // ★ カート全体の「工事つき本体」の数を先に数える ★
  //   組ID を持たない追加工事の行を判断するのに使う（reconcileGroup の第2引数）。
  const pairedQtyAll = lines.filter(needsInstall).reduce((n, l) => n + l.quantity, 0);

  // 組ごとに独立して突き合わせる。1周で複数の組を直せるので、
  // 組の数だけリロードが増えることはない（reloadOnce の上限に当たりにくい）
  for (const group of groupBySet(lines)) {
    if (await reconcileGroup(group, pairedQtyAll)) changed = true;
  }
  return changed;
}

/**
 * 1つの組（または組ID を持たない行のまとまり）を突き合わせる。
 *
 * @returns 実際に直したら true
 */
async function reconcileGroup(lines, pairedQtyAll) {
  // ★ ここに渡るのは1組ぶんだけ ★
  //   組ID を持つ行は「本体1台とその工事・追加工事・保証」、
  //   持たない行はまとめて1つ（この変更より前に作られたカート）。
  //   減らす相手が必ず同じ組から選ばれるので、機種をまたいだ取り違えが起きない。
  //
  // ★ 組ID を持たない行のまとまりは、この変更の前と同じ動きのまま ★
  //   合計数で突き合わせ、cart.items 順の先頭から減らす。工事の行が2つ以上あると
  //   取り違える（残った本体に別ティアの工事費が付く）が、**そこを変えない**のは
  //   代わりに「何もしない」を選ぶと、工事の付いていない工事つき本体が残り、
  //   工事費（取替え 6.3kW以上で ¥31,590）を丸ごと取り逃がす注文が成立するため。
  //   ±¥5,000 の取り違えより損失が大きい。組ID を載せる前に作られたカートは
  //   14日ほどで入れ替わるので、そこまでは従来どおりの挙動で通す。
  //   新しい入口を増やさないよう、組ID の無い工事行が複数あるカートでは
  //   工事の 🗑 を出さない（snippets/cart-products.liquid）。

  const installs = lines.filter(isInstallLine);
  const pairedBodies = lines.filter(needsInstall);
  const warranties = lines.filter(isWarrantyLine);
  const bodies = lines.filter(isBodyLine);
  const additionals = lines.filter(isAdditionalLine);

  // ★ 工事つきの本体が無くなったら追加工事も消す ★
  //   追加工事だけ残ると「工事だけ買う」注文になる。数量は金額を表しているので
  //   0 にする（減らすと請求額が変わる）。
  //
  // ★ bodies ではなく pairedBodies で見る（2026-08-25 修正）★
  //   追加工事は写真判定で出た「標準工事への追加」なので、**工事つきの本体**が
  //   無ければ意味を持たない。bodies は「工事でも保証でも追加工事でもない行」なので
  //   **本体のみ（宅配・requires_shipping=true）も数に入る**。
  //   そのため次の順で操作すると、工事が無いのに追加工事だけが残って請求された:
  //     1. 工事つき本体A ＋ 標準工事 ＋ 追加工事 が入ったカートに、
  //        商品ページの購入ボックスから「本体のみ」B を足す
  //        （モーダル経由ではないので aicon-install-quote.js の clearAiconLines は走らない）
  //     2. 本体A を消す
  //        → bodies に B が居るので追加工事は消えず、
  //          pairedBodies は空なので標準工事だけが消える
  //        → 残り = 本体B（宅配）＋ 追加工事 ¥3,630 ＋ 保証
  //   さらに 2026-08-25 に追加工事の行から 🗑 とステッパーを外した
  //   （snippets/cart-products.liquid）ため、**お客様が自分で消す手段が無い**。
  // ★ 組ID を持たない追加工事は、カート全体で判断する（2026-08-26 修正）★
  //   `extensions/aicon-cart-transform` が追加工事を「一式 ×1」にまとめると、
  //   まとめた親行は **merge の attributes しか持たない**（子行の properties は
  //   item_components 側へ移る）。組ID を引けないまま自分の組だけを見ると、
  //   工事つき本体が1つも居ないグループに落ちて**入れた直後に全部消される**。
  //
  //   ★ item_components に頼らない ★
  //     setIdOf() は item_components からの引き直しも試すが、
  //     `/cart.js` がこのフィールドを返すという記載は公式のリファレンスに無い
  //     （載っているのは `has_components` まで・2026-08-26 確認）。
  //     引けなかったときに黙って消さないよう、カート全体の数を代わりに使う。
  //
  //   ★ まとめると組の区別はそもそも失われる ★
  //     Function は `cart.lines.filter(isAdditionalWorkLine)` で**カート内の全部**を
  //     1行にまとめるため、複数台でも追加工事は1行になる。
  //     ∴ 組ごとに attribution することは原理的にできず、カート全体で見るのが正しい。
  const groupPairedQty = pairedBodies.reduce((n, l) => n + l.quantity, 0);
  const hasSetId = lines.some((line) => setIdOf(line) !== null);
  const pairedQtyForAdditional =
    hasSetId || pairedQtyAll === undefined ? groupPairedQty : pairedQtyAll;

  if (additionals.length > 0 && pairedQtyForAdditional === 0) {
    let changed = false;
    for (const line of additionals) {
      if (await setQuantity(line.key, 0)) changed = true;
    }
    return changed;
  }

  // ★ 保証は本体より多くならない ★
  //   本体を消しても保証だけ残ると「保証だけ買う」注文になる。工事の有無とは無関係に、
  //   本体（工事つき・本体のみのどちらでも）の合計数を上限にする。
  const bodyQty = bodies.reduce((n, l) => n + l.quantity, 0);
  const warrantyQty = warranties.reduce((n, l) => n + l.quantity, 0);
  if (warrantyQty > bodyQty) {
    // 減らす方向のみ。足りない側を増やすと、消したはずの保証が勝手に戻る
    return reduceTo(warranties, warrantyQty - bodyQty);
  }

  // ★ 工事の行が無い場合も見る ★
  //   工事だけ削除されたカート（工事つき本体だけが残った状態）を素通りさせると、
  //   工事費を頂かないまま工事を約束した注文になる（取替え small で 26,590円）。
  //   本体のみ（宅配）だけのカートは両方 0 になり、ここで抜ける。
  if (installs.length === 0 && pairedBodies.length === 0) return false;

  const needQty = pairedBodies.reduce((n, l) => n + l.quantity, 0);
  const installQty = installs.reduce((n, l) => n + l.quantity, 0);
  if (needQty === installQty) return false;

  // ★ 必ず「多いほうを減らす」★
  //   足りないほうを増やすと、お客様が消したはずのものが勝手に戻り、
  //   さらに請求額が増える。減らす方向なら、意図せず高くなることはない。
  if (installQty > needQty) {
    // 本体が消えた／減らされた → 工事を合わせる（needQty=0 なら工事も全部消える）
    return reduceTo(installs, installQty - needQty);
  } else {
    // 工事が消えた／減らされた → 工事つき本体を合わせる（installQty=0 なら本体も消える）
    return reduceTo(pairedBodies, needQty - installQty);
  }
}

/** 指定した行たちから合計 over 個を減らす */
/** @returns 1行でも実際に変えられたら true（変えられなければ reload しない） */
async function reduceTo(lines, over) {
  let remaining = over;
  let changed = false;
  for (const line of lines) {
    if (remaining <= 0) break;
    const next = Math.max(0, line.quantity - remaining);
    remaining -= line.quantity - next;
    if (await setQuantity(line.key, next)) changed = true;
  }
  return changed;
}

/**
 * 1行の数量を変える。
 *
 * ★ 成否を返す（2026-08-25 追加）★
 *   以前は結果を見ずに捨てていた。`cart/change.js` が 422 を返すと
 *   カートは変わらないのに reconcile() が true を返し、run() が reload する。
 *   リロード後に同じ 422 → また reload …で**カートページが無限にリロードし続ける**。
 *   Shopify はバンドルの子行（`can_update_quantity: false`）を 422 で拒否するため、
 *   バンドルを有効にした日に踏む（aicon-cart-line-rank.liquid が
 *   「AiCON はいまバンドルを使っていないが先に合わせておく」と書いている）。
 *
 * @returns 実際にカートが変わったら true
 */
async function setQuantity(key, quantity) {
  try {
    const res = await fetch(`${routeRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

let running = false;

/**
 * リロードの打ち切り。
 *
 * reconcile() は「実際にカートを変えられたとき」だけ true を返すようになったので
 * 原理的にはループしない。ただし直せない状態が残ったときに**カートページが
 * 開けなくなる**のは最悪なので、上限を置いて必ず止まるようにする。
 * 直せないカートは、そのまま表示して人が見られる状態にするほうが安全。
 */
const RELOAD_KEY = 'aicon:cart-pair:reloads';
const RELOAD_LIMIT = 3;

function reloadOnce() {
  let n = 0;
  try {
    n = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    // プライベートモード等で読めない。上限を諦めて1回だけ進む
  }
  if (n >= RELOAD_LIMIT) return;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(n + 1));
  } catch {
    // 書けないなら回数を数えられない。無限になるより進まないほうを選ぶ
    return;
  }
  window.location.reload();
}

async function run() {
  // 自分の変更でイベントが再発火する。二重に走らせない
  if (running) return;
  running = true;
  try {
    if (await reconcile()) {
      // 数量を直したので、テーマが描いている行と合わなくなる。読み直すのが確実
      reloadOnce();
    } else {
      // 直すところが無い＝落ち着いた。次の入店のために回数を戻す
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        // 消せなくても実害はない（上限に当たるだけ）
      }
    }
  } catch {
    // 通信断。次のカート操作でもう一度試す（放置しても請求は Shopify 側が正）
  } finally {
    running = false;
  }
}

/**
 * カート更新イベントを受けて突き合わせる。
 *
 * ★ イベントは cart/change.js を投げる**前**に発火する ★
 * テーマは `assets/component-cart-items.js` で
 *
 *   this.dispatchEvent(new CartLinesUpdateEvent({ ..., promise: deferred.promise }))
 *   fetch(cart_change_url, ...)          // ← 送信はこのあと
 *
 * の順で動く。そのため受け取った瞬間に /cart.js を読むと**削除前のカート**が返り、
 * 「本体1 : 工事1 で揃っている」と判断して何もせずに終わる。
 * 本体を消しても工事の行だけが残っていたのはこれが原因（2026-08-20）。
 *
 * テーマ自身のリスナー（cart-icon.js / header-actions.js / component-cart-items.js）は
 * どれも `event.promise` を待ってから数える。同じ待ち方に揃える。
 *
 * promise を持たない経路（別タブ、古いテーマ、他アプリの発火）もあるので、
 * 無ければ従来どおりその場で走らせる。
 */
function onCartLinesUpdate(event) {
  const promise = event?.promise;
  if (!promise || typeof promise.then !== 'function') {
    run();
    return;
  }
  // reject（通信失敗・在庫エラー）のときはカートが変わっていないので走らせない
  promise.then(run, () => {});
}

// テーマのカート更新イベント（assets/standard-events.d.ts の cartLinesUpdate）
document.addEventListener('shopify:cart:lines-update', onCartLinesUpdate);

// 別タブで操作された場合や、イベントを出さない経路のための保険
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
